import React, { useState } from 'react';
import { sendPasswordReset } from '../../services/auth';
import AuthPageShell, { AuthFormHeading, AuthRule } from './AuthPageShell';
import Button from '../ui/Button';
import Input from '../ui/Input';

interface ForgotPasswordFormProps {
    onSwitchToLogin: () => void;
}

function ForgotPasswordForm({ onSwitchToLogin }: ForgotPasswordFormProps) {
    const [email, setEmail] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSent, setIsSent] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email) {
            setError('Please enter your email');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await sendPasswordReset(email);
            setIsSent(true);
        } catch (err: any) {
            setError(err.message || 'Failed to send reset email');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthPageShell>
            <AuthFormHeading title="Reset Password" />

            {isSent ? (
                <p className="mt-5 text-lead-sm font-semibold text-[#6E6E6E]">
                    If an account exists for <span className="text-ink-secondary">{email}</span>, a link to set a
                    new password is on its way. Check your inbox and spam folder.
                </p>
            ) : (
                <>
                    <p className="mt-5 text-lead-sm font-semibold text-[#6E6E6E]">
                        We&rsquo;ll email you a link to set a new password.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-2 flex flex-col">
                        <Input
                            variant="line"
                            type="email"
                            placeholder="Email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isLoading}
                        />

                        {error && <p className="mt-4 text-meta font-semibold text-alert">{error}</p>}

                        <Button type="submit" variant="primary" size="lg" fullWidth className="mt-6" disabled={isLoading}>
                            {isLoading ? 'Sending…' : 'Send Reset Link'}
                        </Button>
                    </form>
                </>
            )}

            <AuthRule className="mt-7" />

            <button
                type="button"
                onClick={onSwitchToLogin}
                disabled={isLoading}
                className="mt-[18px] self-start text-[13px] text-[#6E6E6E] underline transition-colors hover:text-ink-secondary"
            >
                Back to Log In
            </button>
        </AuthPageShell>
    );
}

export default ForgotPasswordForm;
